// Presentational primitives for the Focus flow. No business logic lives here —
// these are the Typeform-shaped building blocks (frame, headline, options,
// buttons, the escape-hatch message bar) that every step composes from.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type FormEvent,
  type ReactNode,
  type Ref,
} from "react";

import { uiProse } from "@loom/shared/brand";

import { useT } from "../i18n/index.js";
import { stripMarkdownInline } from "../lib/strip-markdown";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";
import { LOGO_SRC } from "../ui/logo";

import { useFocusRail } from "./FocusRailContext";

/* ─── Icons (inline, no dependency) ──────────────────────────────────── */

const svg = (path: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
export const IconChevronLeft = () => svg(<polyline points="15 18 9 12 15 6" />);
export const IconArrowRight = () => svg(<><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>);
export const IconAlert = () => svg(<><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>);
export const IconCheck = () => svg(<polyline points="20 6 9 17 4 12" />);
export const IconPlus = () => svg(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>);
export const IconSearch = () => svg(<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>);
export const IconCreate = () => svg(<><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" /></>);
export const IconRefine = () => svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></>);
export const IconImplement = () => svg(<><circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M6 21V9a9 9 0 0 0 9 9" /></>);
export const IconClose = () => svg(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>);
export const IconChat = () => svg(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />);

/* ─── Frame ──────────────────────────────────────────────────────────── */

// The drawer's element id, referenced by the toggle's `aria-controls`. One
// `.loom-focus` root is mounted at a time, so a constant is unambiguous.
const FOCUS_RAIL_ID = "focus-session-rail";

export interface FocusStatus {
  readonly tone: "idle" | "live" | "work" | "bad";
  readonly label: string;
}

interface FocusFrameProps {
  /** Changing this re-triggers the fade-up animation (one per step). */
  readonly stepKey: string;
  /** 0..1 progress fill; omit to hide the bar. */
  readonly progress?: number;
  readonly onBack?: () => void;
  readonly status?: FocusStatus;
  readonly wide?: boolean;
  readonly children: ReactNode;
  /** Optional escape-hatch row pinned to the bottom. */
  readonly messageBar?: ReactNode;
  /**
   * Float the app logo, centered, in the top half of the screen — decoupled
   * from the left-aligned content column. Used on the entry screens (project
   * picker + main menu).
   */
  readonly heroLogo?: boolean;
  /**
   * Render the chrome toggle for the session-history drawer.
   *
   * OFF by default, and that default is the point. The drawer is supplied per
   * PROJECT, so before this flag every screen inside one carried the toggle —
   * the create wizard's steps, the issue pickers, implement and refine
   * included. Those are linear flows (they are the frames that pass
   * `progress`), and a doorway out of the flow in the corner of every step
   * contradicts the progress bar beside it. Refine had to render an apology for
   * it — "you're in a conversation about #N, it isn't listed here" — which is a
   * control telling you it is on the wrong screen.
   *
   * So the toggle is opt-in and only chat, whose subject IS the list, takes it.
   * The main menu reaches the same drawer through a choice card instead
   * (`useOpenRail`), where destinations on that screen already live.
   */
  readonly railToggle?: boolean;
  /**
   * Quiet account chrome in the top bar — the account link
   * and the appearance control. The top bar is the one row every viewport
   * shows without scrolling, and it sits outside the step, so it never competes
   * with the step's own question. On a narrow screen it wraps to its own line
   * under the bar.
   */
  readonly chromeAside?: ReactNode;
}

export function FocusFrame({
  stepKey,
  progress,
  onBack,
  status,
  wide = false,
  children,
  messageBar,
  heroLogo = false,
  railToggle = false,
  chromeAside,
}: FocusFrameProps) {
  const t = useT();
  // The session-history drawer, supplied by the surrounding project surface.
  // `null` on `/focus` and in every test that renders a frame directly, and a
  // null rail adds neither the toggle nor the drawer.
  const rail = useFocusRail();
  const [railOpen, setRailOpen] = useState(false);
  const railPanelRef = useRef<HTMLElement | null>(null);
  // The control that opened the drawer, captured at open time rather than being
  // the toggle by assumption: the drawer now has two openers (the chat chrome's
  // toggle and the menu's choice card) and focus has to return to whichever one
  // was used, not to a toggle that may not be on this screen at all.
  const railOpenerRef = useRef<HTMLElement | null>(null);
  const openRail = useCallback((opener?: HTMLElement | null) => {
    railOpenerRef.current = opener ?? null;
    setRailOpen(true);
  }, []);
  // Closing returns focus to the control that opened the drawer; otherwise it is
  // left wherever it wandered inside a panel that no longer exists.
  const closeRail = useCallback(() => {
    setRailOpen(false);
    railOpenerRef.current?.focus();
  }, []);
  // The drawer is modal. The scrim stops the mouse; this stops the KEYBOARD —
  // without it Tab walks the whole step behind a 28%-opacity scrim, so a focus
  // ring lands on controls the user cannot see and Enter fires them for real.
  useEffect(() => {
    if (!railOpen) return undefined;
    // Focus the panel itself (not the first row) so the drawer's own label is
    // what gets announced on open.
    railPanelRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        closeRail();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = railPanelRef.current;
      if (panel === null) return;
      // Re-read per keystroke: the panel's content changes as the list loads
      // and as a pick resolves.
      const stops = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = stops[0] ?? panel;
      const last = stops[stops.length - 1] ?? panel;
      const active = document.activeElement;
      if (active === null || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [railOpen, closeRail]);
  return (
    <div className="loom-focus">
      <div className="focus-progress" aria-hidden>
        {progress !== undefined && (
          <div
            className="focus-progress-fill"
            style={{ width: `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%` }}
          />
        )}
      </div>

      <div
        className={
          "focus-chrome" + (chromeAside !== undefined ? " has-aside" : "")
        }
      >
        <div className="focus-chrome-left">
          {onBack ? (
            <button type="button" className="focus-back" onClick={onBack}>
              <IconChevronLeft /> {t("common.back")}
            </button>
          ) : (
            <span className="focus-brand">
              <span className="focus-brand-dot" /> {t("common.brand")}
            </span>
          )}
          {rail !== null && railToggle && (
            // The label is carried by `aria-label`, not by the text: the chrome
            // renders a short form on a narrow viewport, and the control's name
            // must not change with the width it is read at. It also does not
            // change with `railOpen`: the open drawer physically covers this
            // button (it spans x=0-320 at a higher stacking level), so a
            // "Close" name here advertises a control no pointer can reach —
            // and the `aria-modal` drawer hides it from assistive tech anyway.
            // The drawer's own close button is the close control.
            <button
              type="button"
              className="focus-rail-toggle"
              aria-expanded={railOpen}
              aria-controls={FOCUS_RAIL_ID}
              aria-label={t("focus.rail.open")}
              onClick={(e) =>
                railOpen ? closeRail() : openRail(e.currentTarget)
              }
            >
              <span className="focus-rail-toggle-label">
                {t("focus.rail.open")}
              </span>
              <span className="focus-rail-toggle-label is-short">
                {t("focus.rail.openShort")}
              </span>
            </button>
          )}
        </div>
        {chromeAside !== undefined && (
          <div className="focus-chrome-aside">{chromeAside}</div>
        )}
        <div className="focus-chrome-right">
          {status && (
            <span
              className={
                "focus-status-pill" +
                (status.tone === "live"
                  ? " is-live"
                  : status.tone === "work"
                    ? " is-work"
                    : status.tone === "bad"
                      ? " is-bad"
                      : "")
              }
            >
              <span className="dot" />
              {status.label}
            </span>
          )}
        </div>
      </div>

      <div className="focus-stage">
        {/* Centered, top-half app mark — decoupled from the left-aligned column
            so it reads as a hero on the entry screens. */}
        {heroLogo && (
          <div className="focus-hero">
            <img className="focus-hero-logo" src={LOGO_SRC} alt="" aria-hidden />
            <span className="focus-hero-beta">Beta</span>
          </div>
        )}
        {/* keyed so React remounts → CSS fade-up replays every step */}
        <div key={stepKey} className={`focus-col focus-anim${wide ? " is-wide" : ""}`}>
          {children}
        </div>
      </div>

      {messageBar}

      {/* Fixed overlay, last child on purpose: `.loom-focus` is a fixed flex
          COLUMN, so a drawer in the flow would become a full-width band. */}
      {rail !== null && railOpen && (
        <>
          <div className="focus-rail-scrim" onClick={closeRail} />
          <aside
            ref={railPanelRef}
            id={FOCUS_RAIL_ID}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            className="focus-rail"
            aria-label={t("focus.rail.title")}
          >
            {/* The head belongs to the FRAME, not to the rail's content: the
                close button needs `closeRail`, which lives here, and every
                state the rail can be in — list, empty, load failure, a terminal
                refusal that takes the whole panel over — needs the same way
                out. Close comes FIRST because the focus trap reads
                `querySelectorAll` in document order per keystroke, so first
                child is `stops[0]` with no trap change. */}
            <div className="focus-rail-head">
              <button
                type="button"
                className="focus-rail-close"
                aria-label={t("focus.rail.close")}
                onClick={closeRail}
              >
                <IconClose />
              </button>
              <span>{t("focus.rail.title")}</span>
            </div>
            {rail}
          </aside>
        </>
      )}
    </div>
  );
}

/* ─── Text ───────────────────────────────────────────────────────────── */

export const Kicker =({ children, num }: { readonly children: ReactNode; readonly num?: string }) => (
  <div className="focus-kicker">
    {children}
    {num && <span className="num">· {num}</span>}
  </div>
);

export const Headline = ({ children }: { readonly children: ReactNode }) => (
  <h1 className="focus-headline">{children}</h1>
);

export const Help = ({ children }: { readonly children: ReactNode }) =>
  children ? <p className="focus-help">{children}</p> : null;

/**
 * Help line with an inline, collapsible "details" disclosure. `help` is the
 * one-liner that's always shown; `detail` is the 1–2 sentence explanation
 * revealed when the user opens the toggle. Used where the short helper alone
 * can be ambiguous (e.g. "Who this is built for").
 */
export function HelpDetail({
  help,
  detail,
}: {
  readonly help: ReactNode;
  readonly detail?: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!help && !detail) return null;
  return (
    <div className="focus-help-block">
      <p className="focus-help">
        {help}
        {detail && (
          <button
            type="button"
            className="focus-help-toggle"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {t("focus.help.detailsToggle")} <span aria-hidden>{open ? "▲" : "▼"}</span>
          </button>
        )}
      </p>
      {open && detail && <p className="focus-help-detail">{detail}</p>}
    </div>
  );
}

/* ─── Buttons ────────────────────────────────────────────────────────── */

interface NextButtonProps {
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly tone?: "accent" | "ok";
  readonly showEnterHint?: boolean;
}

export function NextButton({
  onClick,
  disabled = false,
  label,
  tone = "accent",
  showEnterHint = true,
}: NextButtonProps) {
  const t = useT();
  return (
    <div className="focus-footer">
      <button
        type="button"
        className={`focus-btn${tone === "ok" ? " is-ok" : ""}`}
        onClick={onClick}
        disabled={disabled}
      >
        {label ?? t("focus.button.ok")} <IconCheck />
      </button>
      {showEnterHint && !disabled && (
        <span className="focus-enter-hint">
          {t("focus.enterHint.press")} <kbd>⌘/Ctrl+Enter</kbd>
        </span>
      )}
    </div>
  );
}

export const GhostButton = ({
  onClick,
  children,
  ariaLabel,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
  readonly ariaLabel?: string;
}) => (
  <button
    type="button"
    className="focus-btn is-ghost"
    onClick={onClick}
    {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
  >
    {children}
  </button>
);

/* ─── Intro choice card ──────────────────────────────────────────────── */

export function BigChoice({
  icon,
  title,
  sub,
  onClick,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  /** Optional second line. The main menu's four choices carry none: their
   *  titles say what each one does. */
  readonly sub?: string;
  readonly onClick: () => void;
}) {
  return (
    <button type="button" className="focus-choice" onClick={onClick}>
      <span className="focus-choice-icon">{icon}</span>
      <span className="focus-choice-title">{title}</span>
      {sub !== undefined && <span className="focus-choice-sub">{sub}</span>}
    </button>
  );
}

/* ─── Option row (single-select / search results) ────────────────────── */

export function OptionButton({
  label,
  sub,
  hotkey,
  active = false,
  selected,
  onClick,
  onMouseEnter,
  autoFocus,
}: {
  readonly label: string;
  /** Quiet second line — "closed" on a milestone, "your previous answer" on the
   *  option an undo is walking back. It was accepted by the milestone call site
   *  long before it existed here: a JSX spread skips excess-property checks, so
   *  the prop compiled and was dropped on the floor. */
  readonly sub?: string;
  readonly hotkey?: string;
  /** Keyboard/hover highlight — transient, no meaning to assistive tech. */
  readonly active?: boolean;
  /**
   * This option is the field's CURRENT value. Distinct from `active`: it is a
   * statement about the data, so it carries the radio semantics. Passing it at
   * all turns the option into a `radio` — the call site is then declaring the
   * group is a single-choice set (the caller wraps them in a `radiogroup`).
   * Omitted ⇒ a plain button, which is what every non-choice list here is.
   */
  readonly selected?: boolean;
  readonly onClick: () => void;
  readonly onMouseEnter?: () => void;
  /** Take the focus on mount (the panel's launched issue in the search step). */
  readonly autoFocus?: boolean;
}) {
  const isSelected = selected === true;
  return (
    <button
      type="button"
      className={`focus-option${active || isSelected ? " is-active" : ""}`}
      {...(autoFocus === true ? { autoFocus: true } : {})}
      // The second click of a double-click is not a second choice. On a Focus
      // step the first click advances to the next question, and its second
      // click would land on whatever option now sits under the cursor — a
      // priority or a milestone the user never read. Keyboard activation reports
      // `detail === 0` and is untouched.
      onClick={(e) => {
        if (e.detail > 1) return;
        onClick();
      }}
      {...(selected !== undefined
        ? { role: "radio", "aria-checked": isSelected }
        : {})}
      {...(onMouseEnter ? { onMouseEnter } : {})}
    >
      {hotkey && <span className="focus-option-key">{hotkey}</span>}
      <span className="focus-option-body">
        <span className="focus-option-title">{label}</span>
        {sub && <span className="focus-option-sub">{sub}</span>}
      </span>
      <span className="focus-option-check">
        <IconCheck />
      </span>
    </button>
  );
}

/* ─── Thinking dots ──────────────────────────────────────────────────── */

export const ThinkingDots = ({ label }: { readonly label?: string }) => (
  <span className="focus-thinking">
    <span className="focus-dots">
      <span />
      <span />
      <span />
    </span>
    {label}
  </span>
);

/* ─── Faded scrollback strip ─────────────────────────────────────────── */

export interface FocusHistoryItem {
  readonly id: string;
  /** "you" / "loom" are real turns; "meta" is light activity (files, roles, gates). */
  readonly role: "you" | "loom" | "meta";
  readonly text: string;
}

/**
 * The last few messages preceding the live step, rendered as a quiet recap above
 * it. Older entries fade out (opacity 0.8 at the newest → 0.3 at the oldest) so
 * the eye lands on the current content while recent context stays glanceable.
 */
export function FocusHistory({
  items,
}: {
  readonly items: ReadonlyArray<FocusHistoryItem>;
}) {
  const t = useT();
  if (items.length === 0) return null;
  const n = items.length;
  return (
    <div className="focus-history">
      {items.map((m, i) => (
        <div
          key={m.id}
          className={`focus-history-msg is-${m.role}`}
          // newest (last) ≈ 0.8, oldest (first) = 0.3, linear in between.
          style={{ opacity: n <= 1 ? 0.8 : 0.3 + (0.5 * i) / (n - 1) }}
        >
          {m.role !== "meta" && (
            <span className="focus-history-who">
              {m.role === "you" ? t("common.you") : t("common.loom")}
            </span>
          )}
          {/* Loom turns are Markdown, but a clipped one-liner can't render
              block elements — strip the syntax instead. User text is literal. */}
          <span className="focus-history-text">
            {m.role === "loom" ? uiProse(stripMarkdownInline(m.text)) : m.text}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Escape-hatch message bar ───────────────────────────────────────── */

export function MessageBar({
  onSend,
  disabled = false,
  placeholder,
  secondary,
  value: controlledValue,
  onValueChange,
  readOnly = false,
  autoFocus = false,
  onFocusChange,
  inputRef,
}: {
  readonly onSend: (text: string) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  /**
   * A SECOND destination for the same bytes. The primary send is claimed by
   * whatever step is pinned (it is that question's "in your own words" box); this
   * one claims nothing. Same text, same trim, different attribution — see
   * `sendFree`'s `mode` in RefineFlow.
   */
  readonly secondary?: {
    readonly label: string;
    readonly onSend: (text: string) => void;
  };
  /**
   * Opt-in controlled mode: pass BOTH and the parent owns the text, including
   * the clear after a send. It exists so a surface can hold the user's own
   * bytes in place while it waits for the row they belong in — clearing on
   * submit would throw them away before anything durable had them.
   *
   * The mode is fixed per call site (FocusChat and RefineFlow always pass
   * both), so it never flips mid-life, which is where the
   * controlled/uncontrolled bugs live. RefineFlow owns it so the text survives
   * the bar folding to a note link and back — the two are different components,
   * and a bar that owned its text lost it the moment Loom's answer arrived.
   */
  readonly value?: string;
  readonly onValueChange?: (next: string) => void;
  /**
   * Held, not dead. `readOnly` keeps the text black, selectable and focused and
   * does not dismiss a soft keyboard; `disabled` would grey the user's own
   * sentence out and drop the caret.
   */
  readonly readOnly?: boolean;
  /** Take the caret on mount — the note bar opens on an explicit click. */
  readonly autoFocus?: boolean;
  /** Whether the caret is anywhere inside the bar (its box or its buttons). */
  readonly onFocusChange?: (focused: boolean) => void;
  /** The text box, for a host that puts text into it and the caret after it
   *  (Focus Chat's "Write my own"). */
  readonly inputRef?: Ref<HTMLTextAreaElement>;
}) {
  const t = useT();
  const [innerValue, setInnerValue] = useState("");
  const controlled = controlledValue !== undefined && onValueChange !== undefined;
  const value = controlled ? controlledValue : innerValue;
  const setValue = controlled ? onValueChange : setInnerValue;
  // Takes the SENDER, so the form submit, the ⌘/Ctrl+Enter path and the
  // secondary button share one trim/guard/clear. Every existing composer test
  // drives the keydown path, which is exactly why it must not fork.
  const submit = (send: (text: string) => void) => {
    const trimmed = value.trim();
    if (trimmed === "" || disabled || readOnly) return;
    send(trimmed);
    // Controlled: the parent clears when the send is DONE, not when it is made.
    if (!controlled) setValue("");
  };
  return (
    <form
      className="focus-msgbar"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        submit(onSend);
      }}
      {...(onFocusChange !== undefined
        ? {
            onFocus: () => onFocusChange(true),
            onBlur: (e: FocusEvent<HTMLFormElement>) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFocusChange(false);
            },
          }
        : {})}
    >
      <AutoGrowTextarea
        {...(inputRef !== undefined ? { ref: inputRef } : {})}
        aria-label={t("focus.messageBar.ariaLabel")}
        placeholder={placeholder ?? t("focus.messageBar.placeholderDefault")}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit(onSend);
          }
        }}
        disabled={disabled}
        readOnly={readOnly}
        {...(autoFocus ? { autoFocus: true } : {})}
      />
      {secondary && (
        <button
          type="button"
          className="focus-msgbar-secondary"
          disabled={disabled || readOnly || value.trim() === ""}
          onClick={() => submit(secondary.onSend)}
        >
          {secondary.label}
        </button>
      )}
      <button type="submit" disabled={disabled || readOnly || value.trim() === ""}>
        {t("common.send")}
      </button>
    </form>
  );
}

/* ─── The message bar, folded to a note link ─────────────────────────── */

/**
 * The composer while Loom's answer is on screen CLOSED.
 *
 * The plan-confirm screen already drops the message bar entirely; a question
 * with Loom's answer cannot, because the user may still have something to say.
 * So the bar folds into one quiet link. It opens a bar that only sends NOTES —
 * a note claims nothing, so nothing typed here can be taken as the pinned
 * question's answer. That is the point: while Loom's answer is closed, no text
 * box is open anywhere on the screen, and "ja" typed into the bar can no longer
 * be attributed to the field the way it was on s11 (dev, 2026-09-25).
 */
export function NoteLinkComposer({
  onSendNote,
  disabled = false,
  value,
  onValueChange,
  onFocusChange,
}: {
  readonly onSendNote: (text: string) => void;
  readonly disabled?: boolean;
  /** The bar's text, owned by the host (it is the SAME text the full bar
   *  shows): a fold must never drop what the user typed. */
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly onFocusChange?: (focused: boolean) => void;
}) {
  const t = useT();
  // Open from the start when it already holds the user's words — a remount
  // hides nothing they typed behind the link.
  const [open, setOpen] = useState(value !== "");
  if (!open) {
    return (
      <div className="focus-msgbar-link">
        <button
          type="button"
          className="focus-link"
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          {t("focus.refine.composer.openNote")}
        </button>
      </div>
    );
  }
  return (
    <MessageBar
      onSend={(text) => {
        onSendNote(text);
        onValueChange("");
        setOpen(false);
      }}
      disabled={disabled}
      placeholder={t("focus.refine.composer.notePlaceholder")}
      value={value}
      onValueChange={onValueChange}
      autoFocus
      {...(onFocusChange !== undefined ? { onFocusChange } : {})}
    />
  );
}
